import { useMemo } from 'react';
import { create } from 'qrcode';

interface Props {
  /** Text encoded in the QR (Fidélité v2: `OTL:<code>`). */
  payload: string;
  /** Side of the square, in CSS px. */
  size?: number;
  /** Accessible name — the SVG is otherwise an unlabeled picture. */
  label: string;
  className?: string;
}

// Quiet zone, in modules. Scanners want ≥ 4; the SVG carries its own white
// border so the code stays readable on any card colour.
const QUIET_ZONE = 4;

function buildPath(payload: string): { path: string; dim: number } {
  const { modules } = create(payload, { errorCorrectionLevel: 'M' });
  const n = modules.size;
  // One horizontal run per stretch of dark modules keeps the `d` string short.
  let path = '';
  for (let row = 0; row < n; row += 1) {
    let col = 0;
    while (col < n) {
      if (!modules.get(row, col)) {
        col += 1;
        continue;
      }
      const start = col;
      while (col < n && modules.get(row, col)) col += 1;
      path += `M${start + QUIET_ZONE} ${row + QUIET_ZONE}h${col - start}v1h-${col - start}z`;
    }
  }
  return { path, dim: n + QUIET_ZONE * 2 };
}

/**
 * A QR code drawn synchronously as SVG — no async render, so the box never
 * flashes empty or shifts the layout. Always black on white, on purpose:
 * it is deliberately NOT themed (a tinted code fails on cheap scanners).
 */
export default function QrCode({ payload, size = 176, label, className }: Props) {
  const { path, dim } = useMemo(() => buildPath(payload), [payload]);
  return (
    <svg
      role="img"
      aria-label={label}
      width={size}
      height={size}
      viewBox={`0 0 ${dim} ${dim}`}
      shapeRendering="crispEdges"
      data-qr-payload={payload}
      className={className}
    >
      <rect width={dim} height={dim} fill="#fff" />
      <path d={path} fill="#000" />
    </svg>
  );
}
